import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TablePagination,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
} from '@mui/material';
import { FilterAlt, Delete } from '@mui/icons-material'; 
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const UserList = () => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [anchorEl, setAnchorEl] = useState(null);
  const isFilterMenuOpen = Boolean(anchorEl);

  const navigate = useNavigate();

  const userTypes = ['individual', 'business'];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = JSON.parse(sessionStorage.getItem('accessToken'));
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/getusers`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const reversedData = response.data.data.reverse(); 
        setTableData(reversedData);
        setFilteredData(reversedData);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSearch = (event) => {
    const keyword = event.target.value.toLowerCase();
    setSearch(keyword);
    applyFilters(keyword, userTypeFilter);
  };


  const handleUserTypeChange = (userType) => {
    const updatedFilter = userTypeFilter.includes(userType)
      ? userTypeFilter.filter((u) => u !== userType)
      : [...userTypeFilter, userType];
    setUserTypeFilter(updatedFilter);
    applyFilters(search, updatedFilter);
  };

  const applyFilters = (keyword, userTypes) => {
    const filtered = tableData.filter((user) => {
      const matchesSearch =
        user.username?.toLowerCase().includes(keyword) ||
        user.phone?.toLowerCase().includes(keyword);

      const matchesUserType =
        userTypes.length > 0
          ? userTypes.includes(user?.usertype) : true;

      return matchesSearch && matchesUserType;
    });

    setFilteredData(filtered);
    setPage(0);
  };

  const handleRowClick = (userId) => {
    navigate(`/admin/userDetails/${userId}`);
  };

  const handleDeleteUser = async (userId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this user?'
    );
    if (!confirmDelete) return;

    try {
      const token = JSON.parse(sessionStorage.getItem('accessToken'));
      await axios.delete(`${process.env.REACT_APP_API_URL}/user/deleteUser/delete/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFilteredData((prev) => prev.filter((user) => user._id !== userId));
      setTableData((prev) => prev.filter((user) => user._id !== userId));
      alert('Order deleted successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to delete user.');
    }
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <div style={{ padding: '40px' }}>
      <Typography variant="h3" style={{
          position: 'relative',
          top: 10,
        }}
        gutterBottom>
        User List
      </Typography>

      <TextField
        label="Search Orders"
        variant="outlined"
        fullWidth
        margin="normal"
        value={search}
        onChange={handleSearch}
        placeholder="Search by username or phone"
      />

      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <IconButton onClick={handleFilterClick}>
          <FilterAlt />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={isFilterMenuOpen}
          onClose={handleFilterClose}
          PaperProps={{
            style: { maxHeight: 400, width: '250px', padding: '10px' },
          }}
        >
          <MenuItem disabled>
            <Typography variant="subtitle1">User Type</Typography>
          </MenuItem>
          {userTypes.map((userType) => (
            <MenuItem key={userType} disableGutters>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userTypeFilter.includes(userType)}
                    onChange={() => handleUserTypeChange(userType)}
                  />
                }
                label={userType}
              />
            </MenuItem>
          ))}
        </Menu>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow
                  key={user._id}
                  hover
                  onClick={() => handleRowClick(user._id)}
                >
                  <TableCell>{user._id}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user?.phone}</TableCell>
                  <TableCell>{user?.usertype}</TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user._id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />
    </div>
  );
};

export default UserList;
